
import { User, UserProfile } from '../../types/auth';
import { API_CONFIG } from '../../config/api.config';
import { roleService } from '../auth/RoleService';

export class PythonUserService {
    private apiUrl: string;

    constructor() {
        this.apiUrl = API_CONFIG.python.apiUrl;
    }

    private getHeaders(): HeadersInit {
        const token = localStorage.getItem('auth_token');
        return {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
        };
    }

    // Helper to map Python backend user to frontend User
    private mapUser(data: any): User {
        return {
            id: data.id.toString(),
            email: data.email,
            name: data.name,
            avatar: data.avatar,
            provider: data.provider || 'google',
            providerId: data.providerId || '',
            role: data.role as 'student' | 'teacher' | 'admin',
            permissions: data.permissions || { capabilities: roleService.getRolePermissions(data.role) },
            isActive: data.is_active !== undefined ? data.is_active : true, // Handle discrepancy
            isVerified: data.is_verified !== undefined ? data.is_verified : true,
            createdAt: new Date(data.created_at),
            updatedAt: new Date(data.updated_at),
            lastLoginAt: data.last_login_at ? new Date(data.last_login_at) : undefined,
            teacherInfo: data.teacher_info,
            studentInfo: data.student_info,
            source_domain: data.source_domain || undefined,
        };
    }

    private mapProfile(data: any): UserProfile {
        if (!data.id) return null as any;

        return {
            id: data.id,
            userId: data.user_id,
            fullName: data.full_name || '',
            dateOfBirth: data.date_of_birth ? new Date(data.date_of_birth) : undefined,
            grade: data.grade ? parseInt(data.grade) : undefined,
            school: data.school,
            phone: data.phone,
            address: data.address,
            bio: data.bio,
            preferences: data.preferences || {
                theme: 'light',
                language: 'vi',
                notifications: true
            },
            createdAt: data.created_at ? new Date(data.created_at) : new Date(),
            updatedAt: data.updated_at ? new Date(data.updated_at) : new Date()
        };
    }

    async getUserById(id: string): Promise<User | null> {
        try {
            const response = await fetch(`${this.apiUrl}/users/${id}`, {
                method: 'GET',
                headers: this.getHeaders(),
            });

            if (!response.ok) {
                if (response.status === 404) return null;
                throw new Error(`Failed to get user: ${response.statusText}`);
            }

            const data = await response.json();
            return this.mapUser(data);
        } catch (error) {
            console.error('PythonUserService: getUserById error', error);
            throw error;
        }
    }

    async updateUser(id: string, updates: Partial<User>): Promise<User> {
        try {
            const updatePayload: any = {
                name: updates.name,
                avatar: updates.avatar,
                role: updates.role,
                permissions: updates.permissions,
                teacher_info: updates.teacherInfo,
                student_info: updates.studentInfo,
                is_active: updates.isActive
            };

            Object.keys(updatePayload).forEach(key => updatePayload[key] === undefined && delete updatePayload[key]);

            const response = await fetch(`${this.apiUrl}/users/${id}`, {
                method: 'PUT',
                headers: this.getHeaders(),
                body: JSON.stringify(updatePayload),
            });

            if (!response.ok) {
                throw new Error(`Failed to update user: ${response.statusText}`);
            }

            const data = await response.json();
            return this.mapUser(data);
        } catch (error) {
            console.error('PythonUserService: updateUser error', error);
            throw error;
        }
    }

    async getUserProfile(userId: string): Promise<UserProfile | null> {
        try {
            const response = await fetch(`${this.apiUrl}/users/${userId}/meta`, {
                headers: this.getHeaders()
            });

            if (!response.ok) {
                return null;
            }

            const data = await response.json();
            if (!data.id) return null;

            return this.mapProfile(data);
        } catch (error) {
            console.error('PythonUserService: getUserProfile error', error);
            return null;
        }
    }

    async updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<UserProfile> {
        try {
            const metaUpdate: any = {
                full_name: updates.fullName,
                date_of_birth: updates.dateOfBirth?.toISOString(),
                grade: updates.grade?.toString(),
                school: updates.school,
                phone: updates.phone,
                address: updates.address,
                bio: updates.bio,
                preferences: updates.preferences
            };

            const response = await fetch(`${this.apiUrl}/users/${userId}/meta`, {
                method: 'PUT',
                headers: this.getHeaders(),
                body: JSON.stringify(metaUpdate)
            });

            if (!response.ok) {
                throw new Error('Failed to update profile');
            }

            const freshProfile = await this.getUserProfile(userId);
            if (!freshProfile) throw new Error('Profile update failed to apply');
            return freshProfile;

        } catch (error) {
            console.error('PythonUserService: updateUserProfile error', error);
            throw error;
        }
    }

    async createUserProfile(profileData: Partial<UserProfile>): Promise<UserProfile> {
        if (!profileData.userId) throw new Error('UserId required');
        return this.updateUserProfile(profileData.userId, profileData);
    }

    async getAllUsers(params?: {
        page?: number;
        limit?: number;
        role?: string;
        status?: string;
        search?: string;
        source_domain?: string;
    }): Promise<{ users: User[], total: number }> {
        try {
            const page = params?.page || 1;
            const limit = params?.limit || 100;
            const skip = (page - 1) * limit;

            const queryParams = new URLSearchParams({
                skip: skip.toString(),
                limit: limit.toString()
            });

            if (params?.role && params.role !== 'all') {
                queryParams.append('role', params.role);
            }

            if (params?.status && params.status !== 'all') {
                queryParams.append('status', params.status);
            }

            if (params?.search) {
                queryParams.append('search', params.search);
            }

            if (params?.source_domain && params.source_domain !== 'all') {
                queryParams.append('source_domain', params.source_domain);
            }

            const response = await fetch(`${this.apiUrl}/users/?${queryParams.toString()}`, {
                headers: this.getHeaders()
            });

            if (!response.ok) {
                if (response.status === 403) return { users: [], total: 0 };
                throw new Error(`Failed to fetch users: ${response.statusText}`);
            }

            const data = await response.json();

            // Handle both old format (array) and new format (object) for compatibility during transition
            if (Array.isArray(data)) {
                return {
                    users: data.map((u: any) => this.mapUser(u)),
                    total: data.length
                };
            }

            return {
                users: data.users.map((u: any) => this.mapUser(u)),
                total: data.total
            };
        } catch (error) {
            console.error('PythonUserService: getAllUsers error', error);
            throw error;
        }
    }

    async getUserStats(): Promise<any> {
        try {
            const response = await fetch(`${this.apiUrl}/users/stats`, {
                headers: this.getHeaders()
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch stats: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            console.error('PythonUserService: getUserStats error', error);
            return {
                totalUsers: 0,
                totalStudents: 0,
                totalTeachers: 0,
                totalAdmins: 0,
                pendingApprovals: 0,
                activeToday: 0
            };
        }
    }

    async updateUserRole(userId: string, role: string): Promise<User> {
        try {
            const response = await fetch(`${this.apiUrl}/users/${userId}/role?role=${role}`, {
                method: 'PUT',
                headers: this.getHeaders()
            });

            if (!response.ok) {
                throw new Error(`Failed to update user role: ${response.statusText}`);
            }

            const updatedUser = await this.getUserById(userId);
            if (!updatedUser) throw new Error('Failed to fetch updated user');

            return updatedUser;
        } catch (error) {
            console.error('PythonUserService: updateUserRole error', error);
            throw error;
        }
    }

    async deleteUser(id: string): Promise<void> {
        try {
            const response = await fetch(`${this.apiUrl}/users/${id}`, {
                method: 'DELETE',
                headers: this.getHeaders()
            });

            if (!response.ok) {
                throw new Error(`Failed to delete user: ${response.statusText}`);
            }
        } catch (error) {
            console.error('PythonUserService: deleteUser error', error);
            throw error;
        }
    }

    async getUserByEmail(email: string): Promise<User | null> {
        try {
            // Encode email for URL
            const response = await fetch(`${this.apiUrl}/users/email/${encodeURIComponent(email)}`, {
                method: 'GET',
                headers: this.getHeaders()
            });

            if (response.status === 404) {
                return null;
            }

            if (!response.ok) {
                throw new Error(`Failed to get user by email: ${response.statusText}`);
            }

            const data = await response.json();
            return this.mapUser(data);
        } catch (error) {
            console.error('PythonUserService: getUserByEmail error', error);
            return null;
        }
    }

    // ✅ Implement createUser
    async createUser(userData: Partial<User>): Promise<User> {
        try {
            const payload = {
                email: userData.email,
                name: userData.name || userData.email, // Default name if missing
                role: userData.role || 'student',
                is_active: userData.isActive !== undefined ? userData.isActive : true,
                is_verified: userData.isVerified !== undefined ? userData.isVerified : true,
            };

            const response = await fetch(`${this.apiUrl}/users/`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify(payload)
            });

            if (response.status === 409) {
                console.warn('User already exists');
                // Could retry fetching?
                const existing = await this.getUserByEmail(userData.email!);
                if (existing) return existing;
            }

            if (!response.ok) {
                throw new Error(`Failed to create user: ${response.statusText}`);
            }

            const data = await response.json();
            return this.mapUser(data);
        } catch (error) {
            console.error('PythonUserService: createUser error', error);
            throw error;
        }
    }

    // --- Lab Device Management ---
    async getUserDevices(userId: string): Promise<any[]> {
        try {
            const response = await fetch(`${this.apiUrl}/users/${userId}/devices`, {
                headers: this.getHeaders()
            });
            if (!response.ok) throw new Error('Failed to fetch devices');
            return await response.json();
        } catch (error) {
            console.error('PythonUserService: getUserDevices error', error);
            return [];
        }
    }

    async computeSecureId(mac: string): Promise<string | null> {
        try {
            const response = await fetch(`${this.apiUrl}/users/compute-secure-id/${mac}`, {
                headers: this.getHeaders()
            });
            if (!response.ok) throw new Error('Failed to compute secure id');
            const data = await response.json();
            return data.secure_id;
        } catch (error) {
            console.error('PythonUserService: computeSecureId error', error);
            return null;
        }
    }

    async bindDevice(userId: string, macAddress: string, deviceName?: string): Promise<any> {
        try {
            const response = await fetch(`${this.apiUrl}/users/${userId}/devices`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify({
                    mac_address: macAddress,
                    device_name: deviceName || 'Bộ thí nghiệm mới',
                    user_id: userId
                })
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to bind device');
            }
            return await response.json();
        } catch (error) {
            console.error('PythonUserService: bindDevice error', error);
            throw error;
        }
    }

    async unbindDevice(deviceId: number): Promise<void> {
        try {
            const response = await fetch(`${this.apiUrl}/users/devices/${deviceId}`, {
                method: 'DELETE',
                headers: this.getHeaders()
            });
            if (!response.ok) throw new Error('Failed to unbind device');
        } catch (error) {
            console.error('PythonUserService: unbindDevice error', error);
            throw error;
        }
    }
}

export const pythonUserService = new PythonUserService();
